import React, { FC, useEffect, useMemo } from 'react';
import {
    Button, TextField, Grid, Typography,
    Dialog, DialogContent, DialogTitle, DialogActions,
    IconButton, Box, FormControlLabel, Checkbox, InputAdornment,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import { z } from 'zod';
import { PaperComponent } from 'shared/ui/draggable/Draggable';
import { AlertCustom } from 'shared/ui/alert/AlertCustom';
import { DurationInput } from 'shared/ui/time/CustomTimeField';
import { createShiftProfileSchema, ShiftProfileFormValues } from 'schemas/ShiftProfile';

interface Props {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: any[];
    initialValues?: Partial<ShiftProfileFormValues>;
    titleKey: string;
    onSubmit: (values: ShiftProfileFormValues) => Promise<void> | void;
    readOnly?: boolean;
}

const DEFAULT_VALUES: ShiftProfileFormValues = {
    name: '',
    description: '',
    is_night: false,
    start_time_min: '',
    start_time_max: '',
    end_time_min: '',
    end_time_max: '',
    duration_standard: '',
    duration_min: '',
    duration_max: '',
};

export const FormShiftProfile: FC<Props> = ({
    open, setOpen, rows, initialValues, titleKey, onSubmit, readOnly = false,
}) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [alerts, setAlerts] = React.useState(false);

    const defaultValues: ShiftProfileFormValues = useMemo(() => ({
        ...DEFAULT_VALUES,
        ...initialValues,
        description: initialValues?.description ?? '',
    }), [initialValues]);

    const schema = useMemo(
        () => createShiftProfileSchema({ t, rows, initialValues, readOnly }),
        [t, rows, initialValues, readOnly],
    );

    type SchemaType = z.infer<ReturnType<typeof createShiftProfileSchema>>;

    const {
        control, register, reset, handleSubmit, watch,
        formState: { errors, isSubmitting, isDirty, isValid },
    } = useForm<SchemaType>({
        defaultValues,
        resolver: zodResolver(schema as any),
        mode: 'onChange',
    });

    const nameWatch = watch('name');

    useEffect(() => {
        if (open) {
            reset(defaultValues);
        }
    }, [open, defaultValues, reset]);

    const changes = () => {
        if (isDirty || (nameWatch ?? '') !== (initialValues?.name ?? '')) {
            setAlerts(true);
        } else {
            setOpen(false);
        }
    };

    const closeForm = () => {
        setOpen(false);
        setAlerts(false);
    };

    const onSubmitForm = handleSubmit(async (values) => {
        try {
            await onSubmit(values as ShiftProfileFormValues);
            closeForm();
        } catch (err) {
            console.error(err);
            enqueueSnackbar(t('general.errors.unexpected'), { variant: 'error' });
        }
    });

    type TimeFieldName =
        | 'start_time_min'
        | 'start_time_max'
        | 'end_time_min'
        | 'end_time_max'
        | 'duration_standard'
        | 'duration_min'
        | 'duration_max';

    const renderTimeField = (
        name: TimeFieldName,
        label: string,
    ) => (
        <Controller
            name={name as any}
            control={control}
            render={({ field }) => (
                <DurationInput
                    fullWidth
                    label={label}
                    min="00:00"
                    max="24:00"
                    value={(field.value as string) ?? ''}
                    onChange={(newValue) => field.onChange(newValue?.raw ?? '')}
                    error={Boolean(errors[name])}
                    helperText={(errors[name]?.message as string) ?? ''}
                    disabled={readOnly}
                />
            )}
        />
    );

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') return;
                changes();
            }}
            disableRestoreFocus
            fullWidth
            maxWidth="md"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t(titleKey)}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alerts && (
                    <AlertCustom alerts={alerts} setAlerts={setAlerts} closeForm={closeForm} t={t} />
                )}
            </Box>
            <DialogContent dividers>
                <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            required
                            label={'Nombre'}
                            placeholder="Ingrese nombre"
                            autoComplete="off"
                            variant="outlined"
                            {...register('name')}
                            error={Boolean(errors.name)}
                            helperText={errors.name?.message ?? ''}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {`${(nameWatch ?? '').length}/100`}
                                        </InputAdornment>
                                    ),
                                },
                                htmlInput: { maxLength: 100 },
                            }}
                            disabled={readOnly}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            label={'Descripción'}
                            placeholder="Ingrese descripción"
                            autoComplete="off"
                            variant="outlined"
                            {...register('description')}
                            error={Boolean(errors.description)}
                            helperText={errors.description?.message ?? ''}
                            slotProps={{ htmlInput: { maxLength: 255 } }}
                            disabled={readOnly}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle1" sx={{ mt: 1 }}>Hora de inicio</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        {renderTimeField('start_time_max', 'Máximo')}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        {renderTimeField('start_time_min', 'Mínimo')}
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle1" sx={{ mt: 1 }}>Hora de fin</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        {renderTimeField('end_time_max', 'Máximo')}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        {renderTimeField('end_time_min', 'Mínimo')}
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle1" sx={{ mt: 1 }}>Duración de tiempo</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        {renderTimeField('duration_standard', 'Estándar')}
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        {renderTimeField('duration_min', 'Mínimo')}
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        {renderTimeField('duration_max', 'Máximo')}
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Controller
                            name="is_night"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                            disabled={readOnly}
                                        />
                                    }
                                    label={'Nocturno'}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={() => changes()}>
                    {t('general.buttons.cancel')}
                </Button>
                {!readOnly && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onSubmitForm}
                        loading={isSubmitting}
                        disabled={!isValid || isSubmitting}
                    >
                        {t('general.buttons.save')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
